/**
 * SPEC §5.2 — the measurement run. Three rounds: warm every candidate (the
 * cold timing ranks, never scores), screen the `screen_top` fastest to warm
 * (one timed sample each), refine the top four (two more). Score is the
 * minimum timed sample. The decision itself is the shared pure function.
 */
import { decide, leaderClears, score, type CandidateSamples, type Decision, type ProbeBudget, type ProbeCandidate, type ProbeMode } from '@latency-globe/shared';

export type Measure = (c: ProbeCandidate, timeoutMs: number, signal: AbortSignal) => Promise<number>;

export interface RunProgress { done: number; total: number; phase: 'warm' | 'screen' | 'refine' }

export interface RunResult {
  decision: Decision;
  samples: CandidateSamples[];
  /** the winner's 1-based position in the warm ranking, for §12: near `screen_top` means the screen is too narrow */
  winnerWarmRank: number | null;
}

const REFINE_TOP = 4;
const REFINE_SAMPLES = 2;

/** Upper bound on requests in a run, for the progress bar; refine can stop early. */
export function plannedRequests(candidates: number, budget: ProbeBudget): number {
  const screened = Math.min(candidates, budget.screen_top);
  return candidates + screened + REFINE_SAMPLES * Math.min(REFINE_TOP, screened);
}

/** §5.4 — opaque responses get a cache-buster; readable ones rely on cache: no-store (see worker/hydrate.ts). */
export function probeUrl(url: string, mode: ProbeMode): string {
  if (mode !== 'opaque') return url;
  return `${url}${url.includes('?') ? '&' : '?'}_=${Math.random().toString(36).slice(2)}`;
}

/** TTFB: `fetch` resolves when headers arrive. Infinity for abort, failure or a wrong status. */
export const measureWithFetch: Measure = async (c, timeoutMs, outer) => {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), timeoutMs);
  const onOuter = () => ctl.abort();
  outer.addEventListener('abort', onOuter, { once: true });
  const t0 = performance.now();
  try {
    const res = await fetch(probeUrl(c.url, c.mode), { mode: c.mode === 'opaque' ? 'no-cors' : 'cors', cache: 'no-store', redirect: 'manual', signal: ctl.signal });
    const ttfb = performance.now() - t0;
    void res.body?.cancel().catch(() => {});
    if (c.mode === 'cors' && c.expect_status !== null && res.status !== c.expect_status) return Infinity;
    return ttfb;
  } catch {
    return Infinity;
  } finally {
    clearTimeout(timer);
    outer.removeEventListener('abort', onOuter);
  }
};

/** §5.2 — skip the run entirely on metered or very slow connections. */
export function connectionAllowsProbing(): boolean {
  const c = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  if (!c) return true;
  if (c.saveData) return false;
  return c.effectiveType !== '2g' && c.effectiveType !== 'slow-2g';
}

async function pool<T>(items: T[], concurrency: number, fn: (t: T) => Promise<void>, signal: AbortSignal): Promise<void> {
  let i = 0;
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (i < items.length && !signal.aborted) { const it = items[i++]!; await fn(it); }
  }));
}

export async function runProbe(
  candidates: ProbeCandidate[],
  budget: ProbeBudget,
  measure: Measure,
  onProgress: (p: RunProgress) => void,
  outer: AbortSignal = new AbortController().signal,
): Promise<RunResult> {
  const ctl = new AbortController();
  const stop = () => ctl.abort();
  outer.addEventListener('abort', stop, { once: true });
  const budgetTimer = setTimeout(stop, budget.total_ms);
  const signal = ctl.signal;
  const samples = new Map<string, CandidateSamples>(candidates.map((c) => [c.code, { code: c.code, samples: [] }]));
  const byCode = new Map(candidates.map((c) => [c.code, c]));
  const warm = new Map<string, number>();
  const total = plannedRequests(candidates.length, budget);
  let done = 0;
  const tick = (phase: RunProgress['phase']) => onProgress({ done: ++done, total, phase });
  let warmOrder: ProbeCandidate[] = [];

  try {
    // Round 0 — warm: pay connection costs. The cold timing carries DNS, TCP and
    // TLS, so it never scores, but it ranks well enough to choose what to time.
    await pool(candidates, budget.warm_concurrency, async (c) => {
      const ms = await measure(c, budget.per_request_ms, signal);
      if (!signal.aborted) warm.set(c.code, ms);
      tick('warm');
    }, signal);
    // A candidate that failed to warm is eliminated, as a failed timed sample would be.
    warmOrder = candidates.filter((c) => Number.isFinite(warm.get(c.code) ?? Infinity)).sort((a, b) => warm.get(a.code)! - warm.get(b.code)!);
    // Round 1 — screen: one timed sample each for the fastest to warm.
    await pool(warmOrder.slice(0, budget.screen_top), budget.timed_concurrency, async (c) => {
      const ms = await measure(c, budget.per_request_ms, signal);
      if (!signal.aborted) samples.get(c.code)!.samples.push(ms);
      tick('screen');
    }, signal);
    // Round 2 — refine the top four; stop early once the leader clears `clear`.
    const ranked = [...samples.values()].filter((s) => Number.isFinite(score(s))).sort((a, b) => score(a) - score(b)).slice(0, REFINE_TOP);
    const work: ProbeCandidate[] = [];
    for (let round = 0; round < REFINE_SAMPLES; round++) for (const s of ranked) work.push(byCode.get(s.code)!);
    await pool(work, budget.timed_concurrency, async (c) => {
      if (leaderClears([...samples.values()])) return;
      const ms = await measure(c, budget.per_request_ms, signal);
      if (!signal.aborted) samples.get(c.code)!.samples.push(ms);
      tick('refine');
    }, signal);
  } finally {
    clearTimeout(budgetTimer);
    outer.removeEventListener('abort', stop);
  }

  const all = [...samples.values()];
  const decision = decide(all);
  const winnerWarmRank = decision.kind === 'winner' ? warmOrder.findIndex((c) => c.code === decision.code) + 1 : null;
  return { decision, samples: all, winnerWarmRank };
}
